import React from "react";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import IconButtons from "../../components/IconButtons/IconButtons";
import {
  calander_active_icon,
  dashboard_active_icon,
  delete_active_icon,
  delete_disabled_icon,
  down_arrow_icon,
  edit_active_icon,
  edit_disabled_icon,
  laptop_active_icon,
  listing_icon,
  plus_active_completed_icon,
  plus_active_ongoing_icon,
  plus_active_scheduled_icon,
  spoc_active_icon,
  trainee_active_icon,
  up_arrow_icon,
  user_setting_active_icon,
  users_active_icon,
} from "../../constants/icons";
import { font_13, getBootcampStatus, getDate } from "./../../utils/utils";
import BootcampModuleExpand from "./BootcampModuleExpand";
import Loader from "../../components/Loader/Loader";
import { Link, useNavigate } from "react-router-dom";
import Constants from "../../constants/constants";

const { ACTIVE, COMPLETED } = Constants;

// Helper function to map permission keys to display names
const mapPermissionKey = (key) => {
  const permissionMap = {
    spoc: "SPOC",
    sessionPresenter: "Session Presenter",
    mentor: "Mentor",
    attendee: "Attendee",
    exerciseReviewer: "Exercise Reviewer",
    assessmentInterviewer: "Assessment Interviewer",
  };
  return permissionMap[key] || null;
};

// Helper function to get true permissions
const getTruePermissions = (permission) => {
  return Object.keys(permission)
    .filter((key) => permission[key])
    .map(mapPermissionKey)
    .filter(Boolean)
    .sort((a, b) => a?.localeCompare(b));
};

// Helper function to get bootcamp state flags
const getBootcampStateFlags = (bootcamp) => {
  return {
    isBootcampScheduled: bootcamp?.status === "SCHEDULED",
    isBootcampActive: bootcamp?.status === "ACTIVE",
    isBootcampConcluded: bootcamp?.status === "COMPLETED",
    isBootcampCancelled: bootcamp?.status === "CANCELLED",
  };
};

// Helper function to get status icon
const getStatusIcon = (statusName) => {
  if (statusName === ACTIVE) return plus_active_ongoing_icon;
  if (statusName === COMPLETED) return plus_active_completed_icon;
  return plus_active_scheduled_icon;
};

// Component for Status Badge
const StatusBadge = ({ bootcampStatus }) => {
  return (
    <Box
      className="module-left-container"
      sx={{
        backgroundColor: bootcampStatus?.color,
        color: bootcampStatus?.text,
      }}
    >
      <Typography
        variant="font_15_bold"
        fontWeight={500}
        className="module-left-head module-left-head-nowrap"
      >
        {bootcampStatus?.name}
      </Typography>
    </Box>
  );
};

// Component for Action Buttons in Header
const HeaderActionButtons = ({
  bootcamp,
  isHr,
  canViewBootcampActions,
  isBootcampConcluded,
  isBootcampActive,
  expandIndex,
  handleListAssessments,
  handleEditBootcamp,
  handleDeleteModalOpen,
  handleDashboardOpen,
}) => {
  return (
    <Box>
      {(bootcamp?.permission?.spoc || bootcamp?.permission?.attendee || isHr) && (
        <IconButtons
          height={font_13}
          width={font_13}
          image={dashboard_active_icon}
          handleClick={(e) => {
            e.stopPropagation();
            handleDashboardOpen(bootcamp);
          }}
          classList="margin-right-1"
          tooltip="Dashboard"
        />
      )}
      {(bootcamp?.permission?.spoc || isHr) && (
        <IconButtons
          height={font_13}
          width={font_13}
          image={listing_icon}
          handleClick={(e) => handleListAssessments(e, bootcamp)}
          classList="margin-right-1"
          tooltip="List Assessments"
        />
      )}
      {canViewBootcampActions && (
        <>
          <IconButtons
            height={font_13}
            width={font_13}
            image={edit_active_icon}
            handleClick={(e) => handleEditBootcamp(e, bootcamp)}
            classList={`margin-right-1 ${
              isBootcampConcluded ? "cursor-auto" : ""
            }`}
            tooltip="Edit Bootcamp"
            disabledImage={edit_disabled_icon}
            disabled={isBootcampConcluded}
            disabledTooltip={
              isBootcampConcluded ? "Bootcamp is completed" : null
            }
          />
          <IconButtons
            height={font_13}
            width={font_13}
            image={delete_active_icon}
            handleClick={(e) => handleDeleteModalOpen(e, bootcamp, "bootcamp")}
            tooltip="Delete Bootcamp"
            classList={`margin-right-1 ${
              isBootcampActive || isBootcampConcluded ? "cursor-auto" : ""
            }`}
            disabledImage={delete_disabled_icon}
            disabled={isBootcampActive || isBootcampConcluded}
            disabledTooltip={
              isBootcampConcluded
                ? "Bootcamp is completed"
                : isBootcampActive
                ? "Bootcamp is active"
                : null
            }
          />
        </>
      )}
      <IconButtons
        height={font_13}
        width={font_13}
        image={expandIndex === bootcamp?.id ? up_arrow_icon : down_arrow_icon}
        tooltip={expandIndex === bootcamp?.id ? "Hide Module" : "Show Module"}
      />
    </Box>
  );
};

// Component for Info Grid Cell
const InfoGridCell = ({
  icon,
  title,
  children,
  gridProps,
  className = "home-dates-container",
}) => {
  return (
    <Grid item {...gridProps} className={className}>
      <Box className="dates-head-container">
        <IconButtons
          width={font_13}
          height={font_13}
          image={icon}
          tooltip={title}
        />
        <Typography variant="font_12_bold" className="dates-head-title">
          {title}
        </Typography>
      </Box>
      <Box className="dates-body-container">{children}</Box>
    </Grid>
  );
};

// Component for Attendees Cell
const AttendeesCell = ({ bootcamp, isHr }) => {
  const canViewAttendees = bootcamp?.permission?.spoc || isHr;
  return (
    <InfoGridCell
      icon={trainee_active_icon}
      title="Attendees"
      gridProps={{
        sm: 2,
        md: 3,
        lg: 1.5,
        xl: 1.5,
        twoK: 1.5,
        fourK: 1.5,
      }}
    >
      <Typography
        color={canViewAttendees && "secondary"}
        variant="font_12_bold"
        className="cursor-pointer"
      >
        <Link to={canViewAttendees && `/bootcamp/${bootcamp.id}/attendees`}>
          {canViewAttendees ? (
            <u>{bootcamp.attendees?.totalResults}</u>
          ) : (
            bootcamp.attendees?.totalResults
          )}
        </Link>
      </Typography>
    </InfoGridCell>
  );
};

// Component for My Roles Cell
const MyRolesCell = ({ truePermissions, isHrOnly, isHrAndAdmin }) => {
  if (isHrOnly || isHrAndAdmin) return null;

  return (
    <InfoGridCell
      icon={spoc_active_icon}
      title="My Roles"
      gridProps={{
        sm: 2,
        md: 4,
        lg: 1.5,
        xl: 2,
        twoK: 2.25,
        fourK: 2,
      }}
    >
      <Typography variant="font_12_bold">
        {truePermissions.toString().split(",").join(", ")}
      </Typography>
    </InfoGridCell>
  );
};

// Component for SPOC Cell
const SpocCell = ({ spocsName, isHr, index }) => {
  return (
    <InfoGridCell
      icon={users_active_icon}
      title="SPOC"
      gridProps={{
        sm: isHr ? 4 : 2,
        md: isHr ? 8 : 4,
        lg: isHr ? 4 : 2.5,
        xl: isHr ? 4 : 2,
        twoK: isHr ? 4.5 : 2.25,
        fourK: isHr ? 4 : 2,
      }}
    >
      <Typography variant="font_12_bold" key={index}>
        {spocsName}
      </Typography>
    </InfoGridCell>
  );
};

// Component for Bootcamp Info Grid
const BootcampInfoGrid = ({
  bootcamp,
  isHr,
  isHrOnly,
  isHrAndAdmin,
  truePermissions,
  spocsName,
  index,
}) => {
  return (
    <Grid container spacing={1} rowSpacing={1} className="home-info-container">
      <InfoGridCell
        icon={user_setting_active_icon}
        title="Competency"
        gridProps={{
          sm: 2,
          md: 3,
          lg: 1.5,
          xl: 1.5,
          twoK: 1.5,
          fourK: 1.5,
        }}
      >
        <Typography variant="font_12_bold">
          {bootcamp?.competency?.name}
        </Typography>
      </InfoGridCell>
      <InfoGridCell
        icon={calander_active_icon}
        title="Start Date"
        gridProps={{
          sm: 2,
          md: 3,
          lg: 1.5,
          xl: 1.5,
          twoK: 1.5,
          fourK: 1.5,
        }}
      >
        <Typography variant="font_12_bold">
          {getDate(bootcamp?.startDate)}
        </Typography>
      </InfoGridCell>
      <InfoGridCell
        icon={calander_active_icon}
        title="End Date"
        gridProps={{
          sm: 2,
          md: 3,
          lg: 1.5,
          xl: 1.5,
          twoK: 1.5,
          fourK: 1.5,
        }}
      >
        <Typography variant="font_12_bold">
          {getDate(bootcamp?.endDate)}
        </Typography>
      </InfoGridCell>
      <AttendeesCell bootcamp={bootcamp} isHr={isHr} />
      <InfoGridCell
        icon={laptop_active_icon}
        title="Modules"
        gridProps={{
          sm: 2,
          md: 3,
          lg: 1.5,
          xl: 1.5,
          twoK: 1.5,
          fourK: 2,
        }}
      >
        <Typography variant="font_12_bold" color="secondary">
          <u>{bootcamp?.modules?.totalResults}</u>
        </Typography>
      </InfoGridCell>
      <MyRolesCell
        truePermissions={truePermissions}
        isHrOnly={isHrOnly}
        isHrAndAdmin={isHrAndAdmin}
      />
      <SpocCell spocsName={spocsName} isHr={isHr} index={index} />
    </Grid>
  );
};

// Component for Action Buttons at Bottom
const BootcampActionButtons = ({
  bootcampStatus,
  canViewActions,
  handleCreateBootcampModalOpen,
  handleCloneModuleModalOpen,
  handleAssociateModuleModalOpen,
  bootcamp,
}) => {
  if (!canViewActions) return null;

  const statusIcon = getStatusIcon(bootcampStatus?.name);
  const statusColor = bootcampStatus?.name.toLowerCase();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        width: "100%",
        flexWrap: "wrap",
      }}
    >
      <Button
        variant="outlined"
        color={statusColor}
        sx={{ flex: 1, minWidth: "200px" }}
        startIcon={
          <IconButtons height={0.625} width={0.625} image={statusIcon} />
        }
        className={`bootcamp-module-add-btn ${statusColor}`}
        onClick={() => handleCreateBootcampModalOpen(bootcamp)}
      >
        <Typography variant="font_11_bold">ADD MODULE</Typography>
      </Button>
      <Button
        variant="outlined"
        color={statusColor}
        sx={{ flex: 1, minWidth: "200px" }}
        startIcon={
          <IconButtons height={0.625} width={0.625} image={statusIcon} />
        }
        className={`bootcamp-module-add-btn ${statusColor}`}
        onClick={() => handleCloneModuleModalOpen(bootcamp)}
      >
        <Typography variant="font_11_bold">CLONE MODULE</Typography>
      </Button>
      <Button
        variant="outlined"
        color={statusColor}
        sx={{ flex: 1, minWidth: "200px" }}
        startIcon={
          <IconButtons height={0.625} width={0.625} image={statusIcon} />
        }
        className={`bootcamp-module-add-btn ${statusColor}`}
        onClick={(e) => handleAssociateModuleModalOpen(e, bootcamp)}
      >
        <Typography variant="font_11_bold">Associate Module</Typography>
      </Button>
    </Box>
  );
};

// Component for SPOC Names
const SpocNames = ({ spocs }) => {
  return spocs
    ?.map((spoc) => spoc?.fullName)
    ?.sort()
    ?.map((name, index, arr) => (
      <React.Fragment key={index}>
        {name}
        {index < arr.length - 1 && ", "}
      </React.Fragment>
    ));
};

// Component for Single Bootcamp Row
const BootcampRow = ({
  bootcamp,
  index,
  bootcampRefs,
  expandIndex,
  handleExpandSubModule,
  isLoading,
  isHr,
  isHrOnly,
  isHrAndAdmin,
  canViewActions,
  canViewBootcampActions,
  bootcampStatus,
  truePermissions,
  spocsName,
  isBootcampConcluded,
  isBootcampActive,
  handleListAssessments,
  handleEditBootcamp,
  handleDeleteModalOpen,
  moduleLoading,
  subModules,
  handleDissociateModalOpen,
  handleEditBootcampModule,
  handleCreateBootcampModalOpen,
  handleCloneModuleModalOpen,
  handleAssociateModuleModalOpen,
  handleAssociateBootcampModalOpen,
  expandBootcampId,
  handleDashboardOpen,
}) => {
  return (
    <Accordion
      ref={(el) => (bootcampRefs.current[index] = el)}
      className={`home-main-cell-container ${bootcampStatus?.name.toLowerCase()}`}
      key={index}
      expanded={expandIndex === bootcamp?.id}
    >
      <AccordionSummary
        id={index && isLoading}
        className="home-cell-container"
        onClick={() => handleExpandSubModule(bootcamp?.id, index)}
      >
        <StatusBadge bootcampStatus={bootcampStatus} />
        <Box className="module-right-container">
          <Box className="home-cell-head-container">
            <Box className="home-cell-main-head">
              <Typography variant="font_15_bold" className="home-cell-heading">
                {bootcamp?.name}
              </Typography>
            </Box>
            <HeaderActionButtons
              bootcamp={bootcamp}
              isHr={isHr}
              canViewBootcampActions={canViewBootcampActions}
              isBootcampConcluded={isBootcampConcluded}
              isBootcampActive={isBootcampActive}
              expandIndex={expandIndex}
              handleListAssessments={handleListAssessments}
              handleEditBootcamp={handleEditBootcamp}
              handleDeleteModalOpen={handleDeleteModalOpen}
              handleDashboardOpen={handleDashboardOpen}
            />
          </Box>
          <BootcampInfoGrid
            bootcamp={bootcamp}
            isHr={isHr}
            isHrOnly={isHrOnly}
            isHrAndAdmin={isHrAndAdmin}
            truePermissions={truePermissions}
            spocsName={spocsName}
            index={index}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails className="expanded-module-collapse">
        {moduleLoading ? (
          <Loader />
        ) : (
          <BootcampModuleExpand
            bootcamp={bootcamp}
            modules={subModules}
            handleDeleteModalOpen={handleDeleteModalOpen}
            handleDissociateModalOpen={handleDissociateModalOpen}
            handleEditBootcampModule={handleEditBootcampModule}
            moduleLoading={moduleLoading}
            isLoading={isLoading}
            isHr={isHr}
            expandBootcampId={expandBootcampId}
            isBootcampConcluded={isBootcampConcluded}
            handleListAssessments={handleListAssessments}
            handleAssociateBootcampModalOpen={handleAssociateBootcampModalOpen}
          />
        )}
      </AccordionDetails>
      <AccordionActions>
        <BootcampActionButtons
          bootcampStatus={bootcampStatus}
          canViewActions={canViewActions}
          handleCreateBootcampModalOpen={handleCreateBootcampModalOpen}
          handleCloneModuleModalOpen={handleCloneModuleModalOpen}
          handleAssociateModuleModalOpen={handleAssociateModuleModalOpen}
          bootcamp={bootcamp}
        />
      </AccordionActions>
    </Accordion>
  );
};

const HomeTable = ({
  bootcampData,
  subModules,
  expandIndex,
  handleExpandSubModule,
  handleDeleteModalOpen,
  handleDissociateModalOpen,
  handleCreateBootcampModalOpen,
  handleEditBootcamp,
  handleEditBootcampModule,
  moduleLoading,
  isLoading,
  isHr,
  isHrOnly,
  bootcampRefs,
  expandBootcampId,
  isHrAndAdmin,
  handleCloneModuleModalOpen,
  handleAssociateModuleModalOpen,
  handleAssociateBootcampModalOpen,
  handleDashboardOpen,
}) => {
  const navigate = useNavigate();

  const handleListAssessments = (e, bootcamp, module) => {
    e.stopPropagation();
    const path = module?.id
      ? `/bootcamp/${bootcamp?.id}/module/${module?.id}/assessments`
      : `/bootcamp/${bootcamp?.id}/assessments`;
    navigate(path);
  };
  return (
    <>
      {bootcampData?.map((bootcamp, index) => {
        const truePermissions = getTruePermissions(bootcamp?.permission || {});
        const stateFlags = getBootcampStateFlags(bootcamp);
        const bootcampStatus = getBootcampStatus(bootcamp?.status);
        const canViewActions =
          bootcamp?.permission?.spoc &&
          (stateFlags.isBootcampActive || stateFlags.isBootcampScheduled);
        const canViewBootcampActions =
          bootcamp?.permission?.spoc && !stateFlags.isBootcampCancelled;
        const spocsName = <SpocNames spocs={bootcamp?.spocs?.results} />;

        return (
          <BootcampRow
            key={index}
            bootcamp={bootcamp}
            index={index}
            bootcampRefs={bootcampRefs}
            expandIndex={expandIndex}
            handleExpandSubModule={handleExpandSubModule}
            isLoading={isLoading}
            isHr={isHr}
            isHrOnly={isHrOnly}
            isHrAndAdmin={isHrAndAdmin}
            canViewActions={canViewActions}
            canViewBootcampActions={canViewBootcampActions}
            bootcampStatus={bootcampStatus}
            truePermissions={truePermissions}
            spocsName={spocsName}
            isBootcampConcluded={stateFlags.isBootcampConcluded}
            isBootcampActive={stateFlags.isBootcampActive}
            handleListAssessments={handleListAssessments}
            handleEditBootcamp={handleEditBootcamp}
            handleDeleteModalOpen={handleDeleteModalOpen}
            moduleLoading={moduleLoading}
            subModules={subModules}
            handleDissociateModalOpen={handleDissociateModalOpen}
            handleEditBootcampModule={handleEditBootcampModule}
            handleCreateBootcampModalOpen={handleCreateBootcampModalOpen}
            handleCloneModuleModalOpen={handleCloneModuleModalOpen}
            handleAssociateModuleModalOpen={handleAssociateModuleModalOpen}
            handleAssociateBootcampModalOpen={handleAssociateBootcampModalOpen}
            expandBootcampId={expandBootcampId}
            handleDashboardOpen={handleDashboardOpen}
          />
        );
      })}
    </>
  );
};

export default HomeTable;
